import { Box, List, ListItem, ListItemButton, ListItemText, Badge } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useMemberUpdatesBadge } from 'src/modules/member/hooks/useMemberUpdatesBadge';
import { useEInvoiceApplicationsBadge } from 'src/modules/invoice/hooks/useEInvoiceApplicationsBadge';
import { useUnmatchedPaymentsBadge } from 'src/modules/invoice/hooks/useUnmatchedPaymentsBadge';
import { useAutogiroUnlinkedBadge } from 'src/modules/invoice/hooks/useAutogiroUnlinkedBadge';
import { useUnaccountedPaymentsBadge } from 'src/modules/invoice/hooks/useUnaccountedPaymentsBadge';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getSysProducts } from 'src/shared/functions/getSysProducts';
import { SysProductYvNameEnum } from 'src/shared/models/enums/sysProductEnums';
import { HeaderSubTitle } from './atoms/headerSubTitle';

export const TodoWidget = () => {
  const { t } = useTranslation(['dashboard']);
  const { currentUser } = useGlobals();

  const sysProducts = getSysProducts(currentUser);
  const hasInvoice = sysProducts.some((x) => x.yvName.toLowerCase() === SysProductYvNameEnum.Invoice.toLowerCase());

  const memberUpdates = useMemberUpdatesBadge();
  const [eInvoiceApplications] = useEInvoiceApplicationsBadge(hasInvoice);
  const unmatchedPayments = useUnmatchedPaymentsBadge(hasInvoice);
  const [unlinkedMandates] = useAutogiroUnlinkedBadge(hasInvoice);
  const unaccountedPayments = useUnaccountedPaymentsBadge(hasInvoice);

  const items = [
    { label: t('dashboard:todoWidget.memberUpdates'), count: memberUpdates, route: RoutesEnum.MemberUpdates, requiresInvoice: false },
    {
      label: t('dashboard:todoWidget.eInvoiceApplications'),
      count: eInvoiceApplications,
      route: RoutesEnum.EInvoiceApplications,
      requiresInvoice: true,
    },
    { label: t('dashboard:todoWidget.unmatchedPayments'), count: unmatchedPayments, route: RoutesEnum.IncomingBankPayment, requiresInvoice: true },
    {
      label: t('dashboard:todoWidget.unaccountedPayments'),
      count: unaccountedPayments,
      route: RoutesEnum.PaymentTransactions,
      requiresInvoice: true,
    },
    {
      label: t('dashboard:todoWidget.unlinkedMandates'),
      count: unlinkedMandates,
      route: RoutesEnum.Autogiro,
      state: { filterLinked: 'unlinked' },
      requiresInvoice: true,
    },
  ].filter((item) => item.count > 0 && (!item.requiresInvoice || hasInvoice));

  return (
    <Box sx={{ p: 2 }}>
      <HeaderSubTitle label={t('dashboard:todoWidget.header')} />
      {items.length === 0 ? (
        <Box mt={1}>
          <em>
            <HeaderSubTitle label={t('dashboard:todoWidget.nothingToDo')} colorTextSecondary />
          </em>
        </Box>
      ) : (
        <List dense disablePadding sx={{ mt: 1 }}>
          {items.map((item) => (
            <ListItem key={item.route} disablePadding divider>
              <ListItemButton component={Link} to={item.route} state={item.state} sx={{ borderRadius: 1, py: 0.5 }}>
                <ListItemText primary={item.label} />
                <Badge
                  badgeContent={item.count}
                  color='error'
                  sx={{ '& .MuiBadge-badge': { position: 'relative', transform: 'none', minWidth: 18, height: 18, fontSize: '0.7rem' } }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};
